import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { PaymentHelper } from '../helpers/PaymentHelper';
import OrderService from '../services/OrderService';
import PaymentStatusPopups from '../components/popups/PaymentStatusPopup/PaymentStatusPopup';
import { PAYMENT_STATUS, POPUP_ROUTES } from '../const';
import { useLocation, matchPath } from 'react-router-dom';
import { ShopContext } from './ShopContext';

export const PaymentContext = createContext(null);

export function PaymentProvider({ children }) {
    const { shop } = useContext(ShopContext)
    const [paymentPopups, setPaymentPopups] = useState([]);

    const location = useLocation();

    const shouldShowPopup = POPUP_ROUTES.some(route =>
        matchPath(location.pathname, {
            path: route,
            exact: true,
        })
    );

    const checkPaymentStatuses = useCallback(async () => {
        const payments = PaymentHelper.getPayments(shop?.shopId);

        const results = await Promise.all(
            payments.map(async (p) => {
                try {
                    const res = await OrderService.getOrderByPayment(
                        p.shop_id,
                        p.payment_id,
                        { payment_secret: p.payment_secret }
                    );

                    if (res?.online_payment) {
                        PaymentHelper.addOrderToLocalStorage(res, p.shop_id);
                    }

                    return {
                        ...p,
                        status: res?.online_payment?.status || res?.status || PAYMENT_STATUS.PENDING,
                        order_id: res?.id,
                    };
                } catch {
                    return { ...p, status: PAYMENT_STATUS.FAILED };
                }
            })
        );

        setPaymentPopups(results);
    }, [shop?.shopId]);

    useEffect(() => {
        checkPaymentStatuses();
    }, [checkPaymentStatuses]);


    const closePopup = useCallback((payment) => {
        if (!payment) return;
        setPaymentPopups(prev =>
            prev.filter(p => p.payment_id !== payment.payment_id)
        );
        PaymentHelper.removePayment(shop?.shopId, payment.payment_id);
    }, [shop?.shopId]);

    const handleRemovePayment = useCallback((paymentId) => {
        if (!paymentId) return;
        setPaymentPopups(prev =>
            prev.filter(p => String(p.payment_id) !== String(paymentId))
        );
        PaymentHelper.removePayment(shop?.shopId, paymentId);
    }, [shop?.shopId]);

    return (
        <PaymentContext.Provider
            value={{
                paymentPopups,
                closePopup,
                handleRemovePayment,
            }}
        >
            {children}
            {shouldShowPopup && paymentPopups.length > 0 && (
                <PaymentStatusPopups />
            )}
        </PaymentContext.Provider>
    );
}
